/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import Icon from 'src/components/Icon'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { useMobxStore } from 'src/state'

type Response = {
  url?: string
  message: string
  onboarded: boolean
}

const fetchConnectedAccountInfo = async (accountId: string) => {
  try {
    const response: Response = await req(`/account/${accountId}/onboarding`)

    return response
  } catch (error: any) {
    throw new Error(JSON.stringify(error))
  }
}

export const ConnectedAccount = observer(() => {
  const {
    me: {
      state: { account },
    },
  } = useMobxStore()
  const [loading, setLoading] = useState(true)
  const [stripeLink, setStripeLink] = useState<string | null>(null)
  const [isOnboarded, setIsOnboarded] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (account?.uid) {
      fetchConnectedAccountInfo(account?.uid)
        .then((response) => {
          if (!response.onboarded) {
            setStripeLink(response.url!)
          }
          setIsOnboarded(response.onboarded)
        })
        .catch((error) => {
          console.error('Error on Fetching Connected Account', error)
          setError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [account])

  if (loading) {
    return (
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 820px;
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        <LoadingSpinner size={3} />
      </div>
    )
  }

  if (error) {
    return (
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 820px;
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        <p>Something goes wrong</p>
      </div>
    )
  }

  if (isOnboarded) {
    return (
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 820px;
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        <p>Account Successfully Connected</p>
      </div>
    )
  }

  return (
    <div
      css={css`
        background-color: ${colors.HEADER};
        padding: 16px;
        border-radius: 8px;
        border: 1px solid ${colors.SOFT_STEEL};
        max-width: 820px;
        margin: 0 auto;
        margin-top: 16px;
      `}
    >
      <a
        css={css`
          color: ${colors.DEFAULT_FLAIR};
          font-size: 16px;
          display: flex;
          gap: 8px;
          align-items: center;
        `}
        href={`${stripeLink}`}
        target='_self'
      >
        <p>Connect your Stripe account</p>
        <Icon name="external-link-alt" color={colors.DEFAULT_FLAIR} />
      </a>
    </div>
  )
})
